import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { THREAD_CAP, threadLimitTone } from '../lib/threadLimits.ts';
import Conversation from './Conversation.tsx';
import EditPanel from './EditPanel.tsx';
import ActivityPanel from './ActivityPanel.tsx';
import {
  ArrowUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Dock,
  LayoutGrid,
  Paperclip,
  Square,
  StopSquare,
} from '../lib/icons.tsx';
import { api } from '../lib/api.ts';
import { startPointerDrag } from '../lib/pointerDrag.ts';
import { useFloatingPanel } from '../lib/useFloatingPanel.ts';
import { useMediaQuery, PHONE_QUERY, COARSE_QUERY } from '../lib/useMediaQuery.ts';
import { usePersistedState } from '../lib/persistedState.ts';
import { uploadAssets } from '../lib/uploadAssets.ts';
import { useActiveTurns } from '../lib/chatTurns.ts';
import { useModelChoice } from '../lib/useModelChoice.ts';
import { FAST_VALUE, PI_VALUE, engineIdFor, sendOptsFor } from '../lib/models.ts';
import { isBusy } from '../lib/userBusy.ts';
import type {
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  RefObject,
} from 'react';
import type { ConversationProps, TranscriptMessage } from './Conversation.tsx';
import type { EditPanelProps } from './EditPanel.tsx';
import type { ActionNotice, JumpTarget, RunningRow } from './ActivityPanel.tsx';
import type { DeckActivity } from '../lib/useDeckActivity.ts';
import { appendToInput, assetRef } from '../lib/assetAsk.ts';
import type { ChatEngineId, ChatModelOption } from '../lib/models.ts';
import type { EngineEffort } from '../lib/useModelChoice.ts';
import { codexEffortLabel, type CodexModel } from '../../shared/codex-models.ts';
import type { StreamingBuffer, TurnOptions, TurnStatus, TurnUsage } from '../lib/chatTurns.ts';
import type { DeckOptions, DeckSection, LayoutFinding, SlideEntry } from '../../shared/deck.ts';
import type { SkillSummary, SkillsPayload } from '../../shared/api.ts';

// Skill index for the "/" autocomplete — fetched once per app session.
let skillsPromise: Promise<SkillSummary[]> | null = null;
function fetchSkills(): Promise<SkillSummary[]> {
  if (!skillsPromise) {
    skillsPromise = api.get<Partial<SkillsPayload>>('/api/skills').then(
      (data) => data?.skills || [],
      () => [],
    );
  }
  return skillsPromise;
}

// Editor conversation scopes (not intake). 'selection' is a constrained deck
// turn, so it gets everything deck scope gets — the engine pick, deck
// skills, the Activity tab, Apply all.
const CONV_SCOPES = new Set(['slide', 'deck', 'selection']);

/** The panel's three verbs. Persisted as a working mode, so it survives
 *  navigating between slides. */
type PanelTab = 'chat' | 'edit' | 'activity';

/**
 * What this bar reads off a scope.
 *
 * DELIBERATELY looser than `ChatScope`: the new-deck intake renders the same
 * bar with a local object of its own whose `kind` is `'new'` — not a chat
 * scope at all, and carrying none of the endpoint paths. `CONV_SCOPES` is
 * what separates the two, so `kind` stays a plain string here.
 */
type BarScope = {
  kind: string;
  slug: string;
  slide?: SlideEntry | null;
  label?: string;
  placeholder?: string;
  key?: string;
};

/**
 * The turn driver. `useChatStream(scope)` satisfies it, and so does the
 * intake's hand-built object — which has no `stop`, because its "turn" is a
 * headless build it cannot cancel from here.
 */
type ChatApi = {
  messages?: TranscriptMessage[] | null;
  streaming?: StreamingBuffer | null;
  status?: TurnStatus | null;
  usage?: TurnUsage | null;
  send: (prompt: string, opts?: TurnOptions) => unknown;
  stop?: (() => void) | null;
  clearThread: () => void;
};

/**
 * A one-shot programmatic send. It waits for the SCOPE to catch up before
 * dispatching, which is what `slideId`/`deck` are for — `send` is bound to
 * the scope at call time, so firing early would hit the previous one.
 */
export type AutoSend = {
  prompt: string;
  /** Wait for deck scope (Merge, Split). */
  deck?: boolean;
  /** Wait for THIS slide's scope (Change template). */
  slideId?: string;
  /** Names the engine outright, so a studio-fired turn never inherits the
   *  pill — see runSend. */
  model?: string | null;
  /** Queue the ask as a PREFILL when the bar cannot send now (a busy chat, an
   *  unreviewed proposal on the slide) rather than dropping or stacking it.
   *  The assets room's "Add to slide" relies on this. */
  seedIfHeld?: boolean;
};

export type CommandBarProps = {
  scope: BarScope;
  chat: ChatApi;
  open: boolean;
  onToggleOpen: (open: boolean) => void;
  /** Switch to deck scope / clear the selection. Null in the intake. */
  onClearScope?: (() => void) | null;
  /** Switch back to the focused slide's scope. Null when there is none. */
  onSelectSlideScope?: (() => void) | null;
  /** A selection-scope turn went out — Canvas drops the selection. */
  onSelectionSent?: (() => void) | null;
  slides?: SlideEntry[];
  onJumpToSlide?: (slideId: string) => void;
  onApplied?: ConversationProps['onApplied'];
  /** Deck scope only. */
  onApplyAll?: ConversationProps['onApplyAll'];
  onRejectAll?: ConversationProps['onRejectAll'];
  empty?: ReactNode;
  /** Extra node inside the conversation (the intake's cards). */
  renderExtra?: ReactNode;
  // See Filmstrip.activeRef: RefObject<T>, not RefObject<T | null>.
  inputRef?: RefObject<HTMLTextAreaElement>;
  disabled?: boolean;
  buildEngineLabel?: string;
  /** One-shot input seed, e.g. "Add a slide to the X section: ". */
  /** A bare string REPLACES the box; the object form joins what is already
   *  typed, for a seed that is a fragment rather than a sentence. */
  prefill?: string | { text: string; mode: 'append' } | null;
  onPrefillConsumed?: () => void;
  autoSend?: AutoSend | null;
  /** False when the bar REFUSED the send (busy, not ready), so a caller
   *  waiting on the turn fails instead of hanging. */
  onAutoSendConsumed?: (dispatched: boolean) => void;
  /** Deck-scope split handoff from a ToolCard overflow chip. */
  onSplitSlide?: ConversationProps['onSplitSlide'];
  /** Deck sections, for the Edit tab's accent context. */
  sections?: DeckSection[];
  /** A direct edit landed on disk → reload the preview iframe. */
  onSlideEdited?: EditPanelProps['onSlideEdited'];
  onToggleHidden?: EditPanelProps['onToggleHidden'];
  onToggleSectionChrome?: EditPanelProps['onToggleSectionChrome'];
  /** Pin/release this slide's WORDS (manifest `copyLocked` → summary refresh).
   *  A decision about the slide, not an edit to it — same family as
   *  `onToggleSectionChrome`. */
  onToggleCopyLocked?: EditPanelProps['onToggleCopyLocked'];
  onToggleLayoutOk?: EditPanelProps['onToggleLayoutOk'];
  /** Deck-level display switches; the per-slide section-label override is only
   *  offered when the deck shows one at all. */
  deckOptions?: DeckOptions | null;
  /** One-shot tab switch: an Empty-variant insert opens Edit, the tray pill
   *  opens Activity. */
  forceTab?: PanelTab | null;
  onForceTabConsumed?: () => void;
  /** `useDeckActivity(slug)`'s state — its presence enables the Activity tab. */
  activity?: DeckActivity | null;
  onJumpToTurn?: (target: JumpTarget) => void;
  onActivityChanged?: (notice?: ActionNotice) => void;
  /** First sight of the Activity tab. Canvas starts the deck-wide measure
   *  then rather than on open: it renders every slide, so it must be asked for. */
  onActivityOpened?: () => void;
  /** Stop pressed on a Running row — Canvas owns the kill and its toast. */
  onStopRun?: (run: RunningRow) => void;
  /** Deck-wide layout findings (`useDeckFindings`) for the Activity tab. */
  findings?: { findings?: Record<string, LayoutFinding[]>; pending?: string[] } | null;
};

/**
 * The right panel docked down the canvas edge (or popped out as a floating
 * window). One selection, two verbs: in slide scope the header tabs between
 * Chat (generative — the conversation + command input) and Edit (direct —
 * text fields and accent swatches for the focused slide). Deck scope is
 * chat-only; structure is direct manipulation in the filmstrip. Docked, it
 * collapses to a slim rail so the preview gets the full width.
 */
export default function CommandBar({
  scope,
  chat,
  open,
  onToggleOpen,
  onClearScope,
  onSelectSlideScope,
  onSelectionSent,
  slides,
  onJumpToSlide,
  onApplied,
  onApplyAll,
  onRejectAll,
  empty,
  renderExtra,
  inputRef: externalInputRef,
  disabled,
  prefill, // one-shot input seed: a string replaces the box ("Add a slide to the X section: "),
  // or { text, mode: 'append' } adds to what is already typed
  onPrefillConsumed,
  buildEngineLabel,
  autoSend, // one-shot programmatic send — fires the turn directly. { prompt, slideId } waits for that slide's scope (Change template); { prompt, deck: true } waits for deck scope (Merge, Split). `model` names the engine explicitly ('sonnet' | 'opus' | 'fast') so a studio-fired turn never inherits the pill — see runSend.
  onAutoSendConsumed, // (dispatched: boolean) => void: false when the bar refused the send (busy/not ready), so a caller waiting on the turn can fail instead of hanging
  onSplitSlide, // deck-scope split handoff from a ToolCard overflow chip
  sections, // deck sections, for the Edit tab's accent context
  onSlideEdited, // direct edit landed on disk → reload the preview iframe
  onToggleHidden, // (slide) => void — park/unpark, shared with the filmstrip kebab
  onToggleSectionChrome, // (slide, next) => Promise — show/hide the top-right section label on this slide (manifest flag → summary refresh)
  deckOptions, // deck-level display switches; the section-label override is only offered when the deck shows one
  onToggleLayoutOk, // (slide, next) => Promise — accept/un-accept this slide's overflow (manifest flag → summary refresh)
  onToggleCopyLocked, // (slide, next) => Promise — pin/release this slide's words (manifest flag → summary refresh)
  forceTab, // one-shot tab switch (Empty-variant inserts open Edit; the tray pill opens Activity)
  onForceTabConsumed,
  activity, // useDeckActivity(slug) result — enables the Activity tab
  onJumpToTurn, // Activity rows navigate through the same handler as the tray
  onActivityChanged, // an Activity action landed on disk → refresh summary + preview
  onStopRun, // Stop pressed on a Running row — Canvas owns the kill + its toast
  findings, // deck-wide layout findings (useDeckFindings) for the Activity tab
  onActivityOpened, // first sight of the Activity tab — Canvas starts the deck-wide measure
}: CommandBarProps) {
  const [input, setInput] = useState('');
  const [attaching, setAttaching] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [skills, setSkills] = useState<SkillSummary[]>([]);
  const [skillSel, setSkillSel] = useState(0);
  const [skillsDismissed, setSkillsDismissed] = useState(false);
  // User-dragged docked-panel width (px). null = CSS default.
  const [convWidth, setConvWidth] = useState<number | null>(() => {
    const v = Number(localStorage.getItem('studio.convWidth'));
    return Number.isFinite(v) && v > 0 ? v : null;
  });
  // True mid-resize so the collapse width-transition is suppressed.
  const [resizing, setResizing] = useState(false);
  // What the server can actually run a turn on: the Claude CLI + credential,
  // the optional OpenCode metered model (⚙ env: LLM_GATEWAY_*), and the
  // ⚙ "Default model". Shared with the modals that fire
  // their own turns — see lib/useModelChoice.js.
  const {
    claudeAvailable,
    fastInfo,
    piInfo,
    codexInfo,
    codexModels,
    codexModelsIssue,
    defaultChoice,
    efforts: effortLadders,
    options: modelOpts,
  } = useModelChoice();
  // ONE engine choice — 'opus' | 'sonnet' | 'fast' | 'pi' — rather than a
  // boolean plus a model. Three engines can run a turn now (2026-08-03) and a
  // flag per engine is how a picker starts contradicting itself. `sendOptsFor`
  // is the single place that knows how each one goes on the wire.
  //
  // It follows the ⚙ "Default model" until the user picks in the pill, after
  // which the manual pick wins for the rest of the session so a settings
  // refresh never yanks the choice back mid-turn.
  const [engineChoice, setEngineChoice] = useState('opus');
  const [codexModelChoice, setCodexModelChoice] = useState('');
  const enginePicked = useRef(false);
  const pickEngine = useCallback((m: string) => {
    enginePicked.current = true;
    setEngineChoice(m);
  }, []);
  useEffect(() => {
    if (!enginePicked.current) setEngineChoice(defaultChoice);
  }, [defaultChoice]);
  // A manual pick outranks the default, but not an engine the studio has
  // stopped offering: switching Sonnet off in ⚙, or an OpenCode key going
  // away, has to take a session that picked it with it — otherwise the pill
  // reads one thing while the turn asks for another.
  useEffect(() => {
    if (!modelOpts.length) return;
    setEngineChoice((m) => (modelOpts.some((o) => o.value === m) ? m : (modelOpts[0]?.value ?? m)));
  }, [modelOpts]);
  // The pill's per-message reasoning rung, PER ENGINE and stored nowhere but
  // this session. It is not a ⚙ setting on purpose: that one is the shared
  // default every user of this studio inherits, and wanting one quick answer
  // must not quietly re-tune everyone else's chat (Jake, 2026-08-03). Kept per
  // engine rather than reset on every switch — trying OpenCode and coming back to
  // Claude is the exact flow this exists for, and losing the rung each time is
  // the annoyance it was meant to remove. Nothing hides: the closed pill names
  // the rung in force, and a reload drops back to the ⚙ default.
  const [effortByEngine, setEffortByEngine] = useState<Partial<Record<ChatEngineId, string | null>>>({});
  const engineId = engineIdFor(engineChoice);
  const selectedCodexModel = codexModels.find((entry) => entry.model === (codexModelChoice || codexInfo?.model));
  const configuredEffort = effortLadders?.codex?.effort;
  const effortLadder =
    engineId === 'codex' && selectedCodexModel
      ? {
          efforts: selectedCodexModel.supportedReasoningEfforts.map((entry) => entry.reasoningEffort),
          effort:
            configuredEffort &&
            selectedCodexModel.supportedReasoningEfforts.some((entry) => entry.reasoningEffort === configuredEffort)
              ? configuredEffort
              : selectedCodexModel.defaultReasoningEffort,
        }
      : effortLadders?.[engineId] || { effort: null, efforts: [] };
  const chatModelOpts = modelOpts.map((entry) =>
    entry.value === 'codex' && codexModelChoice
      ? { ...entry, detail: selectedCodexModel?.displayName || codexModelChoice }
      : entry,
  );
  // A rung the server has stopped offering (the ⚙ model changed under a long
  // session) drops back to the default rather than riding along to a 400.
  const picked = effortByEngine[engineId];
  const effortChoice = picked && effortLadder.efforts.includes(picked) ? picked : null;
  const pickEffort = useCallback(
    (rung: string | null) => setEffortByEngine((prev) => ({ ...prev, [engineIdFor(engineChoice)]: rung })),
    [engineChoice],
  );
  // What a turn sends when nothing overrides it. Claude models ride as
  // `claudeModel` and only in a conversation scope, as they always have; the
  // rung rides alongside whichever engine is chosen.
  const sendOptsForChoice = useCallback(
    (opts?: Record<string, unknown>, inConv?: boolean) => {
      const rung = effortChoice ? { effort: effortChoice } : {};
      if (engineChoice === FAST_VALUE && fastInfo) return { ...opts, fast: true, ...rung };
      // Pi rides the `engine` field, distinguished by its value —
      // see sendOptsFor in lib/models.ts, which is the one place that knows it.
      if (engineChoice === 'codex' && codexInfo)
        return { ...opts, ...sendOptsFor('codex', effortChoice, codexModelChoice) };
      if (engineChoice === PI_VALUE && piInfo) return { ...opts, engine: PI_VALUE, ...rung };
      return opts ? { ...opts, ...rung } : inConv ? { claudeModel: engineChoice, ...rung } : undefined;
    },
    [engineChoice, fastInfo, piInfo, codexInfo, effortChoice, codexModelChoice],
  );
  const sectionRef = useRef<HTMLElement>(null);
  const ownRef = useRef(null);
  const inputRef = externalInputRef || ownRef;
  // Phone: the chat is the bottom pane of a stacked layout — no floating,
  // no drag-resize (the hook keeps the persisted desktop geometry intact).
  const isPhone = useMediaQuery(PHONE_QUERY);
  const isCoarse = useMediaQuery(COARSE_QUERY);
  // Pop-out mode: the whole chat floats as a draggable/resizable panel so
  // the slide preview gets the full height. This is the DEFAULT view; an
  // explicit dock persists as the literal string 'docked'. null = docked.
  // The windowing concern (geometry state, persistence, edge/header drags)
  // lives in useFloatingPanel; popOut here also opens the conversation.
  const {
    float,
    setFloat,
    popOut: popOutGeometry,
    onEdgeResizeStart,
    onFloatDragStart,
  } = useFloatingPanel(sectionRef, isPhone);
  const { messages, streaming, status, usage, send, stop, clearThread } = chat;
  const busy = streaming !== null;

  // Cross-card cap on the preview self-checks' auto-fix round-trips (overflow /
  // overlap / density). Each ToolCard only fires once per finding kind, but
  // every auto-fix produces a NEW proposal card with fresh refs, so a PHANTOM
  // flag (a font-metric near-miss, an overlap false positive) would otherwise
  // loop card→fix→card forever, deleting real content each round (the bug Opus
  // hit). Bound consecutive auto-fixes per user turn; a genuine user message
  // (submit) resets the budget.
  const AUTO_FIX_MAX_ROUNDS = 2;
  const autoFixRounds = useRef(0);
  // Auto-fix round-trips honour the engine toggle exactly like a manual send:
  // the correction stays on the model that made the proposal. Before this, the
  // bare send() fell through to the default engine post-inversion, so an OpenCode
  // (metered) proposal's spacing fix silently escalated to Claude.
  const handleAutoFix = (text: string, opts?: TurnOptions) => {
    if (autoFixRounds.current >= AUTO_FIX_MAX_ROUNDS) return;
    autoFixRounds.current += 1;
    send(text, sendOptsForChoice(opts, CONV_SCOPES.has(scope?.kind)));
  };

  // "Tighten to fit" on an overflowing proposal — user-initiated (overflow is no
  // longer auto-corrected). Bypasses the AUTO_FIX_MAX_ROUNDS cap and the
  // proposalPending gate (it acts ON the pending card, like the Split button),
  // and honours the engine toggle like a normal send.
  const handleManualFix = (text: string) => {
    runSend(text);
  };

  // Slide-scope proposal gate: each pending slide-edit tool card reports itself
  // here (see ToolCard onPendingChange). While any is unresolved, block the
  // user from typing another slide-scope message — they must Apply or Discard
  // the proposal first (prevents stacked, colliding proposals in the thread).
  // Only USER-typed sends are gated (submit); the auto-fix round-trips and the
  // autoSend one-shots (Change template, Split) go through send()/runSend() and
  // stay unblocked. Deck scope has its own batch "Apply all", so gate slide only.
  //
  // Each entry also carries that card's own Keep (null while the card isn't
  // decidable), which is what powers "Keep and carry on" in the held row below:
  // the way to REFINE a change is to keep it and say what to tweak, and the
  // held row is the only part of that flow the user is guaranteed to be looking
  // at — the card itself may be scrolled off (Jake, 2026-08-01).
  const [pendingIds, setPendingIds] = useState<Map<string, (() => void) | null>>(() => new Map());
  const onPendingChange = useCallback((id: string, isPending: boolean, keep: (() => void) | null) => {
    setPendingIds((prev) => {
      const has = prev.has(id);
      if (!isPending && !has) return prev; // no-op — keep the same Map reference
      if (isPending && has && prev.get(id) === keep) return prev;
      const next = new Map(prev);
      if (isPending) next.set(id, keep || null);
      else next.delete(id);
      return next;
    });
  }, []);
  const proposalPending = scope?.kind === 'slide' && pendingIds.size > 0;
  // The newest decidable card — cards register in arrival order, so the last
  // one carrying a Keep is the proposal on screen.
  const keepPending = proposalPending ? [...pendingIds.values()].filter(Boolean).pop() || null : null;
  const [keeping, setKeeping] = useState(false);
  const keepAndCarryOn = async () => {
    if (!keepPending || keeping) return;
    setKeeping(true);
    try {
      await keepPending();
    } finally {
      setKeeping(false);
      inputRef.current?.focus();
    }
  };
  const turnCount = (messages || []).length;
  // How much history the last reply actually re-read: the BIGGEST single
  // prompt in that turn (`contextTokens`), not the sum of its rounds. A turn
  // that reads a primitive, proposes an edit and confirms it makes three
  // model calls over roughly the same context, so `inputTokens` (the turn
  // total, still what the cost line shows) reads ~3x the real thread size —
  // it fired this warning on a thread nowhere near long (Jake, 2026-08-01).
  // Falls back to the total for the Claude path, which reports one figure.
  const lastInputTokens = usage?.contextTokens ?? usage?.inputTokens ?? null;
  const tone = threadLimitTone(turnCount, lastInputTokens);

  // Chat | Edit | Activity tabs. Edit is slide scope only; Activity exists in
  // both editor scopes (not intake — gated on the `activity` prop). The choice
  // persists as a working mode: arrow to the next slide and you're still there.
  const [tabChoice, setTabChoice] = usePersistedState<PanelTab>('studio.rightTab', 'chat');
  const showEditTab = scope?.kind === 'slide' && Boolean(onSlideEdited);
  const showActivityTab = Boolean(activity) && CONV_SCOPES.has(scope?.kind);
  // Badge on the Activity tab: running agents + proposals awaiting review —
  // the panel-level "things in flight or needing you" count (replaces the old
  // floating tray pill). Pulses while anything is still running.
  const liveTurns = useActiveTurns(scope?.slug);
  const runningCount = showActivityTab ? liveTurns.filter((t) => t.state === 'streaming').length : 0;
  const awaitingCount = showActivityTab
    ? (activity?.rows || []).filter((r) => r.status === 'ready' || r.previewing).length
    : 0;
  const activityBadge = runningCount + awaitingCount;
  const tab =
    tabChoice === 'edit' && showEditTab ? 'edit' : tabChoice === 'activity' && showActivityTab ? 'activity' : 'chat';

  // The Activity tab is the only consumer of the deck-wide layout findings, and
  // measuring them means rendering every slide — so that pass waits until the
  // tab is actually looked at. Fires on the RESOLVED tab, so a persisted
  // `studio.rightTab` of 'activity' warms on open exactly as it used to.
  useEffect(() => {
    if (tab === 'activity') onActivityOpened?.();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let alive = true;
    fetchSkills().then((s) => {
      if (alive) setSkills(s);
    });
    return () => {
      alive = false;
    };
  }, []);

  // "/de" → skills for this scope whose name starts with "de". Only while
  // the input is still a bare /word (no trailing args yet).
  const skillMatches = useMemo(() => {
    if (skillsDismissed) return [];
    // Selection turns ride the deck endpoint, so they take deck skills.
    const kind = scope?.kind === 'selection' ? 'deck' : scope?.kind;
    if (kind !== 'slide' && kind !== 'deck') return []; // intake chat has no skills
    const m = input.match(/^\/([a-z0-9-]*)$/i);
    if (!m || skills.length === 0) return [];
    const prefix = (m[1] || '').toLowerCase();
    return skills.filter((s) => s.scope === kind && s.name.startsWith(prefix));
  }, [input, skills, scope?.kind, skillsDismissed]);

  useEffect(() => {
    setSkillSel(0);
    setSkillsDismissed(false);
  }, [input]);

  function insertSkill(name: string) {
    setInput(`/${name} `);
    inputRef.current?.focus();
  }

  // New turn arriving while collapsed or on the Edit tab? Pop the
  // conversation open / switch to Chat so the user sees the reply. Activity is
  // deliberately sticky — watching turns progress there is the point.
  //
  // ...but never off the Edit tab while the user is typing in it. EditPanel is
  // keyed on the slide id below, so the switch UNMOUNTS it, and panel saves
  // happen on blur — the uncommitted text in the focused field is simply gone
  // (lib/userBusy.js). A plain read, not the subscribing hook, and deliberately
  // so: this is a one-shot decision taken when the turn STARTS. Deferring the
  // switch until the user stopped typing would just move the surprise later,
  // and the turn's reply is findable in Chat and Activity whenever they look.
  //
  // NOT on a phone, where the panel is the whole screen and popping it open
  // takes away the slide the user is watching change — which is most of what
  // made the editor feel overwhelming there. The reply must still announce
  // itself, so the collapsed bottom bar grows a live "replying…" and its turn
  // count instead (see the rail below); one tap opens it.
  useEffect(() => {
    if (busy && !open && !isPhone) onToggleOpen(true);
    if (busy && tab === 'edit' && !isBusy()) setTabChoice('chat');
  }, [busy]); // eslint-disable-line react-hooks/exhaustive-deps

  // One-shot tab force: 'edit' (an "Empty — add your own" insert) waits on
  // showEditTab, which naturally defers until the new slide is focused;
  // 'activity' (the tray pill) waits on the Activity tab's gate.
  useEffect(() => {
    if (!forceTab) return;
    if (forceTab === 'edit' && !showEditTab) return;
    if (forceTab === 'activity' && !showActivityTab) return;
    setTabChoice(forceTab);
    if (!open) onToggleOpen(true);
    onForceTabConsumed?.();
  }, [forceTab, showEditTab, showActivityTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Put text in the box, ready for the user to send. It SWITCHES to chat as well
  // as filling it: a seed dropped into a hidden tab is indistinguishable from
  // nothing happening, which is exactly how the geometry badge used to read
  // (Jake, 2026-08-02).
  //
  // 'replace' is for a whole composed ask; 'append' joins what is already typed,
  // for a seed that is a fragment rather than a sentence, and reuses attachFiles'
  // whitespace discipline below so the two ref-inserting paths space identically.
  //
  // Every seeding path goes through here — the prefill prop, EditPanel's styled
  // -text handoff, and autoSend's held fallback — so the three can't drift on
  // which tab they leave you on or whether the box grows to fit.
  function seedInput(text: string | null | undefined, mode: 'replace' | 'append' = 'replace') {
    if (!text) return;
    setTabChoice('chat');
    setInput((v) => (mode === 'append' ? appendToInput(v, text) : text));
    setTimeout(() => {
      inputRef.current?.focus();
      autoGrow();
    }, 50);
  }

  // EditPanel's locked styled-text fields hand off to chat with a seeded ask.
  function askInChat(text: string) {
    seedInput(text);
  }

  // One-shot input seed from the canvas (per-section "+ slide" buttons, the red
  // geometry badge's click-to-fix, and the assets picker's "Add to chat").
  //
  // A bare string REPLACES the box — the seed is a whole composed ask, and the
  // three original callers all assume that. `{ text, mode: 'append' }` adds to
  // what is already typed instead (the assets picker's refs, dropped into an
  // instruction the user is mid-way through writing).
  useEffect(() => {
    if (!prefill) return;
    const { text, mode } = typeof prefill === 'string' ? { text: prefill, mode: 'replace' as const } : prefill;
    if (!text) return;
    seedInput(text, mode);
    onPrefillConsumed?.();
  }, [prefill]); // eslint-disable-line react-hooks/exhaustive-deps

  // Grow the textarea with its content (drag its handle for manual sizing —
  // auto-grow never shrinks below a manual size mid-edit; submit resets).
  function autoGrow() {
    const el = inputRef.current;
    if (!el) return;
    const manual = el.offsetHeight;
    el.style.height = 'auto';
    el.style.height = Math.min(Math.max(el.scrollHeight, manual), Math.round(window.innerHeight * 0.5)) + 'px';
  }

  // Dispatch a message on the current scope.
  //
  // Two kinds of caller, and they resolve the model differently on purpose:
  //
  //  - The user typed it (submit, "Tighten to fit"): no `choice`, so the turn
  //    follows the pill — that IS the pill's job.
  //  - The STUDIO fired it from a modal (Merge, Split, Change template): a
  //    `choice` the modal decided, which wins outright. These turns used to ride
  //    the pill too, so a user who tried OpenCode once got their next merge — N
  //    deletes and a rewrite — from the cheap metered model three messages
  //    later, with nothing in the merge bar saying so.
  //
  // Returns whether it actually sent. A programmatic caller that now WAITS on
  // the turn (the change-template modal's working state) would otherwise hang
  // forever on a refusal here, because the refusal is silent and the autoSend
  // effect used to report success unconditionally.
  function runSend(text: string, choice?: string | null) {
    const t = (text || '').trim();
    if (!t || busy || disabled) return false;
    autoFixRounds.current = 0; // fresh turn — refresh the auto-fix budget
    if (!open) onToggleOpen(true);
    // A model only means anything in conversation scopes (not intake).
    const inConv = CONV_SCOPES.has(scope?.kind);
    if (choice && inConv) send(t, sendOptsFor(choice));
    else send(t, sendOptsForChoice(undefined, inConv));
    // A selection turn captured its slide list at send — Canvas drops the UI
    // selection so the busy dots take over as the visual state.
    if (scope?.kind === 'selection') onSelectionSent?.();
    return true;
  }

  function submit() {
    const text = input.trim();
    if (!text || busy || disabled || proposalPending) return;
    setInput('');
    if (inputRef.current) inputRef.current.style.height = '';
    runSend(text);
  }

  // One-shot programmatic send (Change template's AI turn). Waits until the
  // scope prop has caught up to the target slide before dispatching, so `send`
  // (bound to the scope at call time) can never fire on a stale/previous scope.
  //
  // `seedIfHeld` says what to do when the bar can't take the turn: put the ask in
  // the box rather than dropping it. The assets picker sets it — the user ticked
  // files and pressed a button, so the ask must survive a chat that happens to be
  // busy, and a slide holding an unreviewed proposal must not have a second turn
  // stacked on top of the preview it is showing. Without the flag a refusal is
  // still just a refusal (Change template reports it as a failed run).
  useEffect(() => {
    if (!autoSend) return;
    // A deck send (Split into two slides) waits for the scope switch to deck to
    // land; a slide send (Change template) waits for its target slide's scope.
    if (autoSend.deck) {
      if (scope?.kind !== 'deck') return;
    } else if (scope?.kind !== 'slide' || scope.slide?.id !== autoSend.slideId) {
      return;
    }
    // proposalPending is checked HERE, not in runSend: Tighten and Split act ON
    // the pending card and must stay unblocked.
    const held = autoSend.seedIfHeld && (busy || disabled || proposalPending);
    const dispatched = held ? false : runSend(autoSend.prompt, autoSend.model);
    // The seed waits for the user to press send; it does not fire itself when the
    // bar frees up, which is why `busy` is deliberately not a dependency here.
    if (!dispatched && autoSend.seedIfHeld) seedInput(autoSend.prompt);
    // Report whether it actually went out: a caller waiting on the turn needs to
    // hear a refusal (busy bar, editor not ready) rather than wait for a turn
    // that will never start.
    onAutoSendConsumed?.(dispatched);
  }, [autoSend, scope?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Attach images/videos: upload into the deck's assets/ and drop
  // @assets/<filename> references into the message — the slide tools
  // already know how to place those. (Models here are text-only, so the
  // file content itself isn't sent to the LLM.)
  async function attachFiles(fileList: FileList | File[] | null | undefined) {
    const files = Array.from<File>(fileList || []).filter(Boolean);
    const slug = scope?.slug;
    if (!files.length || !slug || attaching) return;
    setAttaching(true);
    setAttachError(null);
    try {
      const { uploaded, errors } = await uploadAssets(slug, files);
      const refs = uploaded.map((n) => assetRef(n)).join(' ');
      if (refs) {
        setInput((v) => appendToInput(v, refs));
        setTimeout(() => {
          inputRef.current?.focus();
          autoGrow();
        }, 0);
      }
      if (errors.length) {
        setAttachError(errors.map((e) => `${e.name}: ${e.error}`).join(' · '));
      }
    } catch (err) {
      setAttachError(err instanceof Error && err.message ? err.message : 'Upload failed');
    } finally {
      setAttaching(false);
    }
  }

  // Drag the left edge of the docked panel to resize it.
  // Persisted per browser; double-click resets to the CSS default.
  // Pointer capture keeps the drag alive when the cursor crosses the
  // preview iframe (which would otherwise swallow pointer events).
  function onResizeStart(e: ReactPointerEvent<HTMLDivElement>) {
    e.preventDefault();
    const startX = e.clientX;
    const startW = sectionRef.current?.offsetWidth || convWidth || 400;
    const clamp = (w: number) => Math.min(Math.max(w, 300), Math.round(window.innerWidth * 0.6));
    let w = startW;
    setResizing(true);
    startPointerDrag(e, {
      onMove(ev) {
        w = clamp(startW + (startX - ev.clientX));
        setConvWidth(w);
      },
      onEnd() {
        setResizing(false);
        localStorage.setItem('studio.convWidth', String(w));
      },
    });
  }

  function onResizeReset() {
    setConvWidth(null);
    localStorage.removeItem('studio.convWidth');
  }

  // Pop the chat out into a floating panel; the hook owns the geometry, here
  // we also open the conversation so the popped-out panel isn't empty.
  function popOut() {
    popOutGeometry();
    if (!open) onToggleOpen(true);
  }

  function onKeyDown(e: ReactKeyboardEvent<HTMLTextAreaElement>) {
    if (skillMatches.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const delta = e.key === 'ArrowDown' ? 1 : -1;
        setSkillSel((i) => (i + delta + skillMatches.length) % skillMatches.length);
        return;
      }
      if (e.key === 'Tab' || e.key === 'Enter') {
        e.preventDefault();
        const pick = skillMatches[Math.min(skillSel, skillMatches.length - 1)];
        if (pick) insertSkill(pick.name);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setSkillsDismissed(true);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  const showConversation = float ? true : open;

  // Docked & collapsed: a slim rail down the right edge — the preview gets
  // the full width. On a desktop a new reply auto-expands this (see the busy
  // effect above); on a phone it deliberately does not, so the rail is the
  // ONLY thing that can tell you a turn is running. That is why it states it
  // rather than just counting: `aria-live` announces it, and the visible
  // wording changes so the bar is not silently identical while the studio is
  // working on the slide behind it.
  if (!float && !open) {
    return (
      <section ref={sectionRef} className="command-bar command-bar--collapsed" aria-label="Editor">
        <button
          type="button"
          className={'rail-toggle' + (busy ? ' rail-toggle--busy' : '')}
          onClick={() => onToggleOpen(true)}
          title={busy ? 'Studio is working — open the editor' : 'Show the editor'}
          aria-label={busy ? 'Studio is working. Open editor' : 'Show editor'}
        >
          {/* On a phone this is not a rail down the right edge — command-bar.css
              flips it to a bar across the BOTTOM, so it opens upward. A
              left-pointing chevron there describes a movement that does not
              happen. */}
          {isPhone ? <ChevronUp /> : <ChevronLeft />}
          {busy && <span className="rail-toggle__dot" aria-hidden="true" />}
          <span className="rail-toggle__label" role="status" aria-live="polite">
            {busy ? 'Replying…' : 'Editor'}
          </span>
          {turnCount > 0 && <span className="rail-toggle__count">{turnCount}</span>}
        </button>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className={
        'command-bar command-bar--open' +
        (float ? ' command-bar--floating' : '') +
        (resizing ? ' command-bar--resizing' : '')
      }
      style={
        float
          ? { left: float.x, top: float.y, width: float.w, height: float.h }
          : convWidth && !isPhone
            ? { width: convWidth + 'px' }
            : undefined
      }
      /* "Chat" named one of the three tabs INSIDE it rather than the panel — a
         third name for something that already had two. It is the Editor. */
      aria-label="Editor"
    >
      {!float && !isPhone && (
        <div
          className="command-bar__resize"
          onPointerDown={onResizeStart}
          onDoubleClick={onResizeReset}
          title="Drag to resize the panel (double-click to reset)"
        />
      )}
      {float &&
        ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'].map((dir) => (
          <div
            key={dir}
            className={`command-bar__rs command-bar__rs--${dir}`}
            onPointerDown={(e) => onEdgeResizeStart(e, dir)}
          />
        ))}
      {float ? (
        <div className="command-bar__float-head" onPointerDown={onFloatDragStart}>
          {showEditTab || showActivityTab ? (
            <TabStrip
              tab={tab}
              onPick={setTabChoice}
              showEdit={showEditTab}
              showActivity={showActivityTab}
              activityBadge={activityBadge}
              activityLive={runningCount > 0}
            />
          ) : (
            <span>Editor</span>
          )}
          {tab === 'chat' && <TurnMeta turnCount={turnCount} showClear clearThread={clearThread} />}
          <button
            type="button"
            className="iconbtn command-bar__dock"
            onClick={() => {
              setFloat(null);
              onToggleOpen(true);
            }}
            title="Dock the editor to the right edge"
            aria-label="Dock editor"
          >
            <Dock />
          </button>
        </div>
      ) : (
        <div className="command-bar__head">
          <button
            type="button"
            className="iconbtn"
            onClick={() => onToggleOpen(false)}
            title={isPhone ? 'Collapse the editor to a bar' : 'Collapse the editor to a rail'}
            aria-label="Collapse editor"
          >
            {/* Down, not right, on a phone: the panel is a bottom bar there and
                collapsing moves it DOWNWARD. */}
            {isPhone ? <ChevronDown /> : <ChevronRight />}
          </button>
          {showEditTab || showActivityTab ? (
            <TabStrip
              tab={tab}
              onPick={setTabChoice}
              showEdit={showEditTab}
              showActivity={showActivityTab}
              activityBadge={activityBadge}
              activityLive={runningCount > 0}
            />
          ) : (
            <span>Editor</span>
          )}
          {tab === 'chat' && <TurnMeta turnCount={turnCount} showClear clearThread={clearThread} />}
          {!isPhone && !isCoarse && (
            <span
              className="command-bar__popout iconbtn"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                popOut();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  popOut();
                }
              }}
              data-tip="Pop the editor out into a floating panel you can drag anywhere"
              aria-label="Pop out editor"
            >
              ⧉
            </span>
          )}
        </div>
      )}

      {tab === 'edit' && scope?.slide && (
        <EditPanel
          key={scope.slide.id}
          slug={scope.slug}
          slide={scope.slide}
          sections={sections}
          onSlideEdited={onSlideEdited}
          onAskInChat={askInChat}
          onToggleHidden={onToggleHidden}
          onToggleLayoutOk={onToggleLayoutOk}
          onToggleCopyLocked={onToggleCopyLocked}
          onToggleSectionChrome={onToggleSectionChrome}
          deckOptions={deckOptions}
        />
      )}

      {tab === 'activity' && showActivityTab && (
        <ActivityPanel
          slug={scope.slug}
          slides={slides || []}
          activity={activity}
          onJumpToTurn={onJumpToTurn}
          onChanged={onActivityChanged}
          onStopRun={onStopRun}
          findings={findings}
        />
      )}

      {tab === 'chat' && showConversation && (
        <>
          {tone !== 'ok' && (
            <div className={'command-bar__limit command-bar__limit--' + tone} role="status">
              <span className="command-bar__limit-text">
                {tone === 'critical'
                  ? 'This chat is near its cap: the oldest messages are about to drop. Start fresh to keep the AI sharp.'
                  : lastInputTokens != null
                    ? `This chat is getting long (~${Math.round(lastInputTokens / 1000)}k tokens re-read every reply): replies get slower and the AI loses focus. Starting fresh is recommended.`
                    : 'This chat is getting long: replies get slower and the AI loses focus. Starting fresh is recommended.'}
              </span>
              <button
                type="button"
                className="btn btn--ghost btn--sm command-bar__limit-clear"
                onClick={clearThread}
                disabled={busy}
                title="Clears this conversation only: changes already on your slides are kept, and anything still awaiting review stays in the Activity tab."
              >
                Start fresh
              </button>
            </div>
          )}
          <Conversation
            messages={messages}
            streaming={streaming}
            slug={scope?.slug}
            slide={scope?.slide}
            slides={slides}
            onJumpToSlide={onJumpToSlide}
            onApplied={onApplied}
            onApplyAll={onApplyAll}
            onRejectAll={onRejectAll}
            onAutoFix={handleAutoFix}
            onSplitSlide={onSplitSlide}
            onTighten={handleManualFix}
            onPendingChange={onPendingChange}
            empty={empty}
            renderExtra={renderExtra}
            style={{ flex: 1, maxHeight: 'none', minHeight: 0 }} // fill the panel
          />
        </>
      )}

      {tab === 'chat' && skillMatches.length > 0 && (
        <div className="command-bar__skills" role="listbox" aria-label="Skills">
          {skillMatches.map((s, i) => (
            <button
              key={s.name}
              type="button"
              role="option"
              aria-selected={i === skillSel}
              className={'command-bar__skill' + (i === skillSel ? ' command-bar__skill--active' : '')}
              onMouseEnter={() => setSkillSel(i)}
              onClick={() => insertSkill(s.name)}
            >
              <span className="command-bar__skill-name">/{s.name}</span>
              <span className="command-bar__skill-desc">{s.description}</span>
            </button>
          ))}
        </div>
      )}
      {tab === 'chat' && (
        <div className="command-bar__row">
          <ScopeChip
            scope={scope}
            onClearScope={onClearScope}
            onSelectSlideScope={onSelectSlideScope}
            onJumpToSlide={onJumpToSlide}
          />
          {buildEngineLabel ? (
            <span className="model-pill__label" aria-label="Deck build engine">
              Build: {buildEngineLabel}
            </span>
          ) : (
            <ModelPill
              options={chatModelOpts}
              claudeAvailable={claudeAvailable}
              value={engineChoice}
              onPick={pickEngine}
              inConvScope={CONV_SCOPES.has(scope?.kind)}
              effortLadder={effortLadder}
              effort={effortChoice}
              onPickEffort={pickEffort}
              codexModels={codexModels}
              codexModelsIssue={codexModelsIssue}
              codexModel={codexModelChoice}
              codexDefaultModel={codexInfo?.model || 'gpt-6.1-sol'}
              onPickCodexModel={setCodexModelChoice}
            />
          )}
          {status?.kind === 'error' && <span className="command-bar__error error">{status.message}</span>}
          {attachError && <span className="command-bar__error error">{attachError}</span>}
          <span className="command-bar__break" aria-hidden="true" />
          {/* One field, not three boxes: attach, input and send sit INSIDE a
              single bordered composer that carries the focus ring, so the row
              reads as somewhere to type rather than as a toolbar. */}
          <div className={'composer' + (busy || proposalPending ? ' composer--held' : '')}>
            {/* The state is said ONCE, here. It used to be a bold orange line
                AND the same sentence repeated as the input's placeholder. */}
            {(busy || proposalPending) && (
              <div className="composer__state" role="status">
                {busy ? (
                  <>
                    <span className="composer__spinner" aria-hidden="true" />
                    Studio is working
                  </>
                ) : (
                  // Say what to DO, not what's blocked. "Not quite right" is
                  // the common case and its answer is not obvious: keep the
                  // change, then ask for the tweak. The line used to stop at
                  // "to carry on", which named the block and left the refine
                  // flow undiscoverable (Jake, 2026-08-01).
                  // One span, not loose text nodes: this row is a flex
                  // container, so each bare string would become its own flex
                  // item and the sentence would break apart.
                  <span>
                    Keep or discard the change above to carry on.
                    {keepPending && (
                      <>
                        {' '}
                        Not quite right?{' '}
                        <button type="button" className="link" onClick={keepAndCarryOn} disabled={keeping}>
                          {keeping ? 'Keeping…' : 'Keep and carry on'}
                        </button>
                        , then say what to tweak.
                      </>
                    )}
                  </span>
                )}
              </div>
            )}
            {scope?.slug && (
              <input
                ref={fileRef}
                type="file"
                accept="image/*,video/*"
                multiple
                hidden
                onChange={(e) => {
                  attachFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            )}
            <textarea
              ref={inputRef}
              className="command-bar__input"
              rows={1}
              placeholder={busy || proposalPending ? '' : scope?.placeholder || 'Tell the studio what to change…'}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                autoGrow();
              }}
              onKeyDown={onKeyDown}
              onPaste={(e) => {
                const files = Array.from(e.clipboardData?.files || []);
                if (files.length) {
                  e.preventDefault();
                  attachFiles(files);
                }
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                const files = Array.from(e.dataTransfer?.files || []);
                if (files.length) {
                  e.preventDefault();
                  attachFiles(files);
                }
              }}
              disabled={busy || disabled || proposalPending}
            />
            <div className="composer__actions">
              {scope?.slug && (
                <button
                  type="button"
                  className="composer__attach"
                  onClick={() => fileRef.current?.click()}
                  disabled={attaching || busy || disabled || proposalPending}
                  data-tip={
                    'Attach images or video\nUploads to this deck’s assets and references them in your message; paste or drop files works too. With a Vision model set (⚙), the model sees attached images.'
                  }
                  aria-label="Attach files"
                >
                  {attaching ? '…' : <Paperclip size={15} />}
                </button>
              )}
              <span className="composer__spacer" />
              {busy ? (
                // Stopping isn't destructive — it keeps every slide already
                // written — so this is no longer the red danger button.
                <button
                  type="button"
                  className="composer__send composer__send--stop"
                  onClick={() => stop?.()}
                  data-tip={'Stop\nCancels the reply. Anything already proposed stays as cards.'}
                  data-tip-side="right"
                  aria-label="Stop generating"
                >
                  {/* Real element, not ::after — that pseudo-element is the
                      tooltip's (see .composer__ring). */}
                  <span className="composer__ring" aria-hidden="true" />
                  <StopSquare size={11} />
                </button>
              ) : (
                <button
                  type="button"
                  className="composer__send"
                  onClick={submit}
                  disabled={disabled || proposalPending || input.trim().length === 0}
                  aria-label="Send"
                >
                  <ArrowUp size={16} strokeWidth={2.2} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {tab === 'chat' && usage && showConversation && (
        <div
          className="command-bar__usage muted small"
          // Totals across every model call the reply took (a read, an edit and
          // its confirmation are three) — so "in" is legitimately a multiple of
          // the thread's size, which the context figure names separately.
          title={
            'Token usage for the last reply, totalled over its rounds' +
            (usage.contextTokens ? ` · biggest single prompt ${usage.contextTokens}` : '')
          }
        >
          {usage.inputTokens} in · {usage.outputTokens} out
          {(usage.cacheReadInputTokens ?? 0) > 0 && ` · ${usage.cacheReadInputTokens} cached`}
        </div>
      )}
    </section>
  );
}

// Chat | Edit | Activity — the verbs on the panel. Edit only in slide scope;
// Activity in both editor scopes. Pointer events stop here so clicking a tab
// in the floating header doesn't start a window drag.
function TabStrip({
  tab,
  onPick,
  showEdit = true,
  showActivity = false,
  activityBadge = 0,
  activityLive = false,
}: {
  tab: PanelTab;
  onPick: (tab: PanelTab) => void;
  showEdit?: boolean;
  showActivity?: boolean;
  /** Running agents + proposals awaiting review — the "in flight or needing
   *  you" count. */
  activityBadge?: number;
  activityLive?: boolean;
}) {
  return (
    <div
      className="command-bar__tabs"
      role="tablist"
      aria-label="Panel mode"
      onPointerDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        role="tab"
        aria-selected={tab === 'chat'}
        className={'command-bar__tab' + (tab === 'chat' ? ' command-bar__tab--active' : '')}
        onClick={() => onPick('chat')}
      >
        Chat
      </button>
      {showEdit && (
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'edit'}
          className={'command-bar__tab' + (tab === 'edit' ? ' command-bar__tab--active' : '')}
          onClick={() => onPick('edit')}
        >
          Edit
        </button>
      )}
      {showActivity && (
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'activity'}
          className={'command-bar__tab' + (tab === 'activity' ? ' command-bar__tab--active' : '')}
          onClick={() => onPick('activity')}
        >
          Activity
          {activityBadge > 0 && (
            <span
              className={'command-bar__tab-badge' + (activityLive ? ' command-bar__tab-badge--live' : '')}
              title={activityLive ? `${activityBadge} running or awaiting review` : `${activityBadge} awaiting review`}
            >
              {activityBadge}
            </span>
          )}
        </button>
      )}
    </div>
  );
}

/**
 * Keeps a menu inside the window, whatever its trigger is doing.
 *
 * CSS can only pick one edge to hang off, and neither edge is right for long:
 * the panel is drag-resizable, it can float, and both triggers change width
 * with their own label (the model name, the slide title), so the menu's x
 * position moves under it. So measure the menu once it's on screen and nudge
 * it back by exactly the overflow — it stays anchored to its trigger until it
 * has to move, then moves the minimum. A resize while a menu is open is only
 * reachable via the window (dragging the panel splitter is a mousedown
 * outside, which closes it), so `resize` is the one event worth re-placing on.
 */
const MENU_GUTTER = 10;
function useEdgeSafeMenu(open: boolean) {
  const menuRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!open) return undefined;
    function place() {
      const node = menuRef.current;
      if (!node) return;
      node.style.transform = '';
      const box = node.getBoundingClientRect();
      const overRight = box.right - (window.innerWidth - MENU_GUTTER);
      const overLeft = MENU_GUTTER - box.left;
      // Right first: a menu too wide for the window keeps its left edge (and
      // so its labels) readable rather than its right.
      const shift = overRight > 0 ? -overRight : overLeft > 0 ? overLeft : 0;
      if (shift) node.style.transform = `translateX(${Math.round(shift)}px)`;
    }
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);
  return menuRef;
}

/**
 * What each rung buys, in the terms the choice is actually made in: time.
 *
 * The numbers are this studio's own measurements, not vendor copy — Claude at
 * low/high on one reasoning-heavy edit (2.4s to first token / 21.1s total vs
 * 26.9s / 42.6s), and the gateway at `none` against any thinking rung (6.5-7.1s
 * with zero reasoning tokens vs 28-51s). See DEFAULT_CLAUDE_CHAT_EFFORT and
 * GATEWAY_EFFORT_LEVELS for the runs behind them.
 *
 * Not every engine offers every rung; a name missing here falls back to a
 * generic tip rather than being hidden, because the server's ladder decides
 * what exists and this table only decides what to SAY about it.
 */
const EFFORT_TIPS: Record<string, string> = {
  none: 'No reasoning at all — the fastest answer this model gives. Good for a rename or a copy tweak, poor for anything it has to work out.',
  minimal: 'Barely any reasoning. Quick answers, shallow edits.',
  low: 'Answers in a few seconds. The rung to reach for when you want a quick reply to a small ask.',
  medium: 'The balance most turns want, and the usual Settings default.',
  high: 'Deliberates properly. Noticeably slower, and worth it on a slide that needs restructuring.',
  xhigh: 'Slower still. For a stubborn slide the lower rungs keep getting wrong.',
  max: 'The deepest this model reasons. Expect to wait.',
  ultra: 'Deeper than max, where the model offers it. Expect to wait a while.',
};

/**
 * Single pill for the chat engine — the one control that says what the next
 * message will run on, and changes it.
 *
 * Three engines can answer a message (2026-08-03): Claude on the subscription
 * (the default, Opus plus Sonnet when the ⚙ toggle offers it), Pi on the
 * ChatGPT plan, and one metered OpenCode model. The pill renders whatever
 * `chatModelOptions` returns rather than knowing any of them by name — an
 * engine appears here by being configured on the server, never by growing
 * another branch in this component. Hides entirely when there is nothing to
 * choose; ONE entry is not nothing, because the pill is also the label.
 *
 * Below the engines sits the active one's REASONING ROW: the same question the
 * ⚙ drawer asks, answered for one message. It lives here because the ⚙ answer
 * is the studio-wide default and this one is not — going to Settings to make a
 * single question answer quickly, then remembering to put it back, is the thing
 * this row removes. The rungs come from the server per engine and per model, so
 * a model with no dial simply has no row.
 */
function ModelPill({
  options,
  claudeAvailable,
  value,
  onPick,
  inConvScope,
  effortLadder,
  effort,
  onPickEffort,
  codexModels,
  codexModelsIssue,
  codexModel,
  codexDefaultModel,
  onPickCodexModel,
}: {
  /** Every engine the server can run, already filtered — see lib/models.ts. */
  options: ChatModelOption[];
  claudeAvailable: boolean;
  /** The ONE engine choice: 'opus' | 'sonnet' | 'fast' | 'pi'. */
  value: string;
  onPick: (choice: string) => void;
  /** A model only means anything in a conversation scope, never the intake. */
  inConvScope: boolean;
  /** This engine's ladder: the rung in force with no override, and the rungs
   *  the pill may offer. Server-derived and per MODEL — an engine with no
   *  `efforts` gets no row at all. */
  effortLadder?: EngineEffort;
  effort?: string | null;
  onPickEffort?: (rung: string | null) => void;
  codexModels: CodexModel[];
  codexModelsIssue: string;
  codexModel: string;
  codexDefaultModel: string;
  onPickCodexModel: (model: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useEdgeSafeMenu(open);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (e.target instanceof Node && !rootRef.current?.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // A Claude model only means anything in a conversation scope (not intake), so
  // outside one the Claude entries drop out and the metered/plan engines — which
  // ride their own flag — stay.
  const opts = options.filter((o) =>
    o.value === 'opus' || o.value === 'sonnet' ? claudeAvailable && inConvScope : true,
  );
  if (!opts.length) return null;
  // `opts.length` is checked above, so there is always a first option.
  const active = opts.find((o) => o.value === value) ?? opts[0]!;
  const rungs = effortLadder?.efforts || [];
  // What the next message will actually think at: the override, else the ⚙
  // default the server reports. Named on the CLOSED pill, because an override
  // nobody can see is worse than no override — it would silently outlive the
  // question it was set for.
  const activeRung = effort || effortLadder?.effort || null;
  // The closed pill marks a metered choice and says nothing about a free one:
  // absence is the safe default, and the marker is a warning rather than a
  // label. It is the last thing seen before pressing send.
  const label =
    (active.detail ? `${active.label} · ${active.detail}` : active.label) +
    (activeRung ? ` · ${activeRung}` : '') +
    (active.metered ? ' · metered' : '');

  function pick(v: string) {
    onPick(v);
    setOpen(false);
  }

  return (
    <div className="model-pill" ref={rootRef}>
      <button
        type="button"
        className={'model-pill__btn' + (open ? ' model-pill__btn--open' : '')}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Active model: ${label}. Click to change.`}
      >
        <span className="model-pill__label">{label}</span>
        <span className="model-pill__caret" aria-hidden="true">
          <ChevronDown />
        </span>
      </button>
      {open && (
        // The listbox is the ENGINE list alone, not the whole menu: the rungs
        // below are a second question with its own answer, and options of a
        // listbox that don't change its value are how a screen reader ends up
        // announcing "model: high".
        <div className="model-pill__menu" ref={menuRef}>
          <div role="listbox" aria-label="Model">
            {opts.map((o, i) => (
              <Fragment key={o.value}>
                {/* A rule between what is free at the point of use and what
                    costs money. Keyed on `metered`, never on the engine's NAME:
                    "OpenCode costs money, Claude does not" was true right up
                    until Claude gained a metered mode. */}
                {i > 0 && o.metered && !opts[i - 1]?.metered && (
                  <div className="model-pill__divider" role="separator" />
                )}
                <ModelOpt
                  label={o.label}
                  detail={o.detail}
                  metered={o.metered}
                  active={active.value === o.value}
                  hint={o.hint}
                  onClick={() => pick(o.value)}
                />
              </Fragment>
            ))}
          </div>
          {active.value === 'codex' && (
            <>
              <div className="model-pill__divider" role="separator" />
              <div className="model-pill__effort">
                <label className="model-pill__effort-label" htmlFor="chat-codex-model">
                  Codex model
                  <span className="model-pill__effort-note">{codexModel ? 'this session only' : 'from Settings'}</span>
                </label>
                <select
                  id="chat-codex-model"
                  aria-label="Chat Codex model"
                  value={codexModel}
                  disabled={!codexModels.length}
                  onChange={(e) => onPickCodexModel(e.target.value)}
                >
                  <option value="">Default · {codexDefaultModel}</option>
                  {codexModel && !codexModels.some((entry) => entry.model === codexModel) && (
                    <option value={codexModel} disabled>
                      {codexModel} · unavailable
                    </option>
                  )}
                  {codexModels.map((entry) => (
                    <option key={entry.model} value={entry.model}>
                      {entry.displayName}
                    </option>
                  ))}
                </select>
                {codexModelsIssue && (
                  <p className="small error" role="alert">
                    {codexModelsIssue}
                  </p>
                )}
              </div>
            </>
          )}
          {/* The active engine's reasoning rungs. Only when there are at least
              two: a model with one rung, or none, has no decision to offer and
              a row of dead chips reads as a control that is broken rather than
              absent. */}
          {rungs.length > 1 && (
            <>
              <div className="model-pill__divider" role="separator" />
              <div className="model-pill__effort">
                <span className="model-pill__effort-label">
                  Thinking
                  {/* Says what the current answer MEANS, per the studio's own
                      rule for controls: which rung, and that it lasts for this
                      session rather than being saved for everyone. */}
                  <span className="model-pill__effort-note">{effort ? 'this session only' : 'from Settings'}</span>
                </span>
                <div className="model-pill__rungs" role="group" aria-label="Reasoning effort">
                  {/* Clearing is a rung of its own, not a second control: the
                      way back to the shared default has to be as reachable as
                      the way off it. */}
                  <button
                    type="button"
                    className={'model-pill__rung' + (!effort ? ' model-pill__rung--active' : '')}
                    aria-pressed={!effort}
                    onClick={() => onPickEffort?.(null)}
                    data-tip={`The Settings default${effortLadder?.effort ? ` (${effortLadder.effort})` : ''}.`}
                  >
                    Default
                  </button>
                  {rungs.map((r) => (
                    <button
                      key={r}
                      type="button"
                      className={'model-pill__rung' + (effort === r ? ' model-pill__rung--active' : '')}
                      aria-pressed={effort === r}
                      onClick={() => onPickEffort?.(r)}
                      data-tip={EFFORT_TIPS[r] || `Reason at "${r}" for the rest of this session.`}
                    >
                      {active.value === 'codex' ? codexEffortLabel(r) : r}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * What this message will change — named in the composer, because the scope is
 * an attribute of the message you are about to send, not of the panel.
 *
 * It used to be a two-segment toggle above the composer whose active side was
 * an 11-character truncation of the slide title ("For the Lov…"). That is a
 * problem beyond size: scope FOLLOWS THE FOCUSED SLIDE (`routes/Canvas.jsx` —
 * a focused slide means slide scope, none means deck), so scrolling the deck
 * silently re-aims the chat, and the two scopes do very different things —
 * one edits a slide, the other adds, deletes and reorders slides and sections.
 * So the chip states the CONSEQUENCE first ("This slide" / "Whole deck") and
 * names the slide second, and it sits under your eyes as you type.
 *
 * Picking the scope you are already in re-focuses that slide in the preview,
 * which is what clicking the old active segment did.
 */
function ScopeChip({
  scope,
  onClearScope,
  onSelectSlideScope,
  onJumpToSlide,
}: Pick<CommandBarProps, 'onClearScope' | 'onSelectSlideScope' | 'onJumpToSlide'> & { scope: BarScope }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useEdgeSafeMenu(open);
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (e.target instanceof Node && !rootRef.current?.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!scope || !CONV_SCOPES.has(scope.kind)) return null;
  // Nothing to switch between (no slide to focus, no way back to the deck):
  // the chip would be a button that does nothing. State it flat instead.
  const canPickSlide = scope.kind === 'slide' || Boolean(onSelectSlideScope);
  const canPickDeck = Boolean(onClearScope) || scope.kind === 'deck';

  const isSlide = scope.kind === 'slide';
  const isSelection = scope.kind === 'selection';
  const slideNo = isSlide ? String((scope.slide?.idx ?? 0) + 1).padStart(2, '0') : null;
  const what = isSlide ? 'This slide' : isSelection ? `${scope.label} selected` : 'Whole deck';
  const which = isSlide ? `${slideNo} ${scope.label}` : null;

  function pick(fn: (() => void) | null | undefined) {
    setOpen(false);
    fn?.();
  }

  return (
    <div className="scope-chip" ref={rootRef}>
      <button
        type="button"
        className={'scope-chip__btn' + (open ? ' scope-chip__btn--open' : '')}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`This message changes: ${what}${which ? `, ${which}` : ''}. Click to change.`}
        title={which || undefined}
      >
        {isSlide || isSelection ? <Square size={12} /> : <LayoutGrid size={12} />}
        <span className="scope-chip__what">{what}</span>
        {which && <span className="scope-chip__which">{which}</span>}
        <span className="scope-chip__caret" aria-hidden="true">
          <ChevronDown />
        </span>
      </button>
      {open && (
        <div className="scope-chip__menu" role="listbox" aria-label="What this message changes" ref={menuRef}>
          <button
            type="button"
            role="option"
            aria-selected={isSlide}
            className={'scope-chip__opt' + (isSlide ? ' scope-chip__opt--active' : '')}
            disabled={!canPickSlide}
            onClick={() => {
              const focused = scope.slide;
              pick(isSlide && focused ? () => onJumpToSlide?.(focused.id) : onSelectSlideScope);
            }}
          >
            <Square size={13} />
            <span>
              <span className="scope-chip__opt-label">This slide</span>
              <span className="scope-chip__opt-hint">
                {isSlide
                  ? `${slideNo} ${scope.label} — click to show it in the preview`
                  : canPickSlide
                    ? 'Rewrite one slide: its copy, layout or primitive'
                    : 'Pick a slide in the filmstrip first'}
              </span>
            </span>
          </button>
          <button
            type="button"
            role="option"
            aria-selected={!isSlide}
            className={'scope-chip__opt' + (!isSlide ? ' scope-chip__opt--active' : '')}
            disabled={!canPickDeck}
            onClick={() => pick(scope.kind === 'deck' ? null : onClearScope)}
          >
            <LayoutGrid size={13} />
            <span>
              <span className="scope-chip__opt-label">Whole deck</span>
              <span className="scope-chip__opt-hint">Add, remove or reorder slides and sections</span>
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

function ModelOpt({
  label,
  detail,
  metered,
  active,
  hint,
  onClick,
}: {
  label: string;
  /** The engine's model name; the plain Claude options carry none. */
  detail?: string;
  /** Charged at API rates — the pill marks it, and never re-derives this
   *  from the engine name (see chatModelOptions). */
  metered?: boolean;
  active: boolean;
  hint?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      className={'model-pill__opt' + (active ? ' model-pill__opt--active' : '')}
      onClick={onClick}
      data-tip={hint || undefined}
    >
      <span className="model-pill__opt-label">{label}</span>
      {detail && <span className="model-pill__opt-detail">{detail}</span>}
      {/* Marked where the choice is made, not explained in a paragraph nobody
          opens. The engine's own hint carries the detail. */}
      {metered && <span className="model-pill__meter">metered</span>}
    </button>
  );
}

// The turn-count chip plus clear-thread link, rendered identically in the
// floating header and the docked header.
function TurnMeta({
  turnCount,
  showClear,
  clearThread,
}: {
  turnCount: number;
  showClear?: boolean;
  clearThread: () => void;
}) {
  // Just the "clear" reset — the bare message count read as a mystery
  // notification badge (Jake, 2026-07-29); the long-thread warning banner
  // already covers "this thread is getting long".
  if (turnCount === 0) return null;
  return (
    <>
      {showClear && (
        <span
          className="command-bar__clear link"
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            clearThread();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              clearThread();
            }
          }}
          title={`Threads keep the most recent history (last ${THREAD_CAP} messages; tool rounds count)`}
        >
          clear
        </span>
      )}
    </>
  );
}
